import { useState, useEffect, useRef } from 'react';
import { Leaf, Recycle } from 'lucide-react';
import { Button } from './ui/button';

const features = [
  {
    icon: Leaf,
    title: 'Ethical Sourcing',
  },
  {
    icon: Recycle,
    title: 'Reusable Materials',
  },
];

export const SustainabilitySection = ({ onRequestQuote }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="sustainability"
      ref={sectionRef}
      className="py-14 md:py-20 bg-[#F5F2ED] border-t border-gray-200 px-5 md:px-0"
      data-testid="sustainability-section"
    >
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left Content */}
          <div
            className={`space-y-5 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <h2 className="text-2xl font-semibold tracking-tight text-[#2B2B2B] leading-tight md:text-3xl lg:text-4xl">
              Sustainability Built<br />
              Into Every Stitch
            </h2>
            <p className="text-[#5A5A5A] text-base leading-relaxed max-w-md">
              Our eco-friendly bags are made from ethically sourced, reusable materials
              with a commitment to responsible production.
            </p>

            {/* Feature Icons - Simple horizontal layout */}
            <div className="flex flex-wrap gap-6 pt-2">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`flex items-center gap-2 transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${(index + 1) * 200}ms` }}
                >
                  <div className="w-8 h-8 rounded-full bg-[#2F4F3E]/10 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-[#2F4F3E]" />
                  </div>
                  <span className="text-sm font-semibold text-[#2B2B2B]">
                    {feature.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image with CTA below */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="rounded-xl overflow-hidden">
              <img
                src={`${process.env.PUBLIC_URL || ''}/Sus_pro.png`}
                alt="Eco-friendly bags and sustainable packaging"
                className="w-full h-[300px] md:h-[350px] object-cover"
                loading="lazy"
                data-testid="sustainability-image"
              />
            </div>

            {/* CTA Button below image */}
            <div className="mt-6">
              <Button
                onClick={onRequestQuote}
                className="w-full md:w-auto bg-green-700 hover:bg-green-800 text-white rounded-xl py-3 shadow-md px-6 text-sm font-medium transition-all duration-200"
                data-testid="sustainability-quote-btn"
              >
                Request Wholesale Quote
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
